import { useCallback, useState } from 'react';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { DisplayNameDialogProps } from 'types/Dialog.types';

interface UseDisplayNameDialogReturn extends DisplayNameDialogProps {
  openDialog: () => void;
}

/**
 * Hook to manage DisplayNameDialog state and logic.
 * Encapsulates open/close state and the confirm handler that updates the deckline's display_name.
 */
const useDisplayNameDialog = (
  selectedDeckline: EnhancedDeckline | null | undefined,
  updatePageDeckline: <K extends keyof EnhancedDeckline>(
    internalDecklineId: string,
    field: K,
    newValue: EnhancedDeckline[K],
  ) => void,
): UseDisplayNameDialogReturn => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /** Opens the dialog if a deckline is currently selected. */
  const openDialog = useCallback(() => {
    if (selectedDeckline) setIsDialogOpen(true);
  }, [selectedDeckline]);

  /** Persists the new display_name to the selected deckline and closes the dialog. */
  const handleConfirm = useCallback(
    (displayName: string | null) => {
      if (selectedDeckline) {
        updatePageDeckline(selectedDeckline.metadata.internalId, 'display_name', displayName);
      }
      setIsDialogOpen(false);
    },
    [selectedDeckline, updatePageDeckline],
  );

  return {
    isDialogOpen,
    setIsDialogOpen,
    openDialog,
    handleConfirm,
    currentDisplayName: selectedDeckline?.display_name ?? null,
    kpiName: selectedDeckline?.kpi_name ?? '',
  };
};

export default useDisplayNameDialog;
